import { useCallback, useEffect, useRef } from 'react';

import { useAssistanceViewContext } from './AssistanceViewContext.tsx';
import { FormField } from './useDocumentConfig.ts';
import { filterFastTrack } from './utils.tsx';
import { ItemRef } from './validationChecks.ts';

const SETTLE_ATTEMPTS = 3;
const SETTLE_DELAY_MS = 150;

const scrollIntoView = (element: HTMLElement) => element.scrollIntoView({ behavior: 'smooth', block: 'start' });

export const useScrollToItem = (itemFormFieldGroups: Record<string, FormField[][]>) => {
    const { formsRef, itemVirtualizerRef, isFastTrackEnabled } = useAssistanceViewContext();
    const settleTimeoutRef = useRef<number>(undefined);

    useEffect(() => () => window.clearTimeout(settleTimeoutRef.current), []);

    return useCallback(
        ({ itemType, itemIndex }: ItemRef) => {
            const findElement = () =>
                formsRef?.current?.querySelector<HTMLElement>(
                    `[data-item-type="${itemType}"] .js-form-item-group[data-item-index="${itemIndex}"]`
                );

            window.clearTimeout(settleTimeoutRef.current);

            const element = findElement();
            if (element) {
                scrollIntoView(element);
                return;
            }

            // Virtualized and off-screen: jump with the virtualizer, then settle on the rendered row
            const groups = itemFormFieldGroups[itemType] || [];
            const displayGroups = isFastTrackEnabled ? filterFastTrack(groups)[0] : groups;
            const displayIndex = displayGroups.findIndex((fields) => fields[0]?.itemIndex === itemIndex);
            if (displayIndex < 0 || !itemVirtualizerRef?.current) return;

            itemVirtualizerRef.current.scrollToIndex(displayIndex, { align: 'start' });

            const settle = (attemptsLeft: number) => {
                settleTimeoutRef.current = window.setTimeout(() => {
                    const renderedElement = findElement();
                    if (renderedElement) scrollIntoView(renderedElement);
                    else if (attemptsLeft > 1) settle(attemptsLeft - 1);
                }, SETTLE_DELAY_MS);
            };
            settle(SETTLE_ATTEMPTS);
        },
        [formsRef, itemVirtualizerRef, isFastTrackEnabled, itemFormFieldGroups]
    );
};
